import { formatDate } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { AddEvent, EditEvent, RemoveEvent } from '@progress/kendo-angular-grid';
import * as _ from 'lodash';
import { interval, Subscription } from 'rxjs';
import { StaffMember } from 'src/app/lobby/shared/models/staff-member';
import { LobbyEntryService } from 'src/app/lobby/shared/services/lobby-entry.service';
import { UserPermissions } from 'src/app/shared/enums/permissionType';
import { GetUserPermissionList, getACLicenseStatus, getSystemConfigurations, getCurrentTenant } from 'src/app/state';
import { State } from 'src/app/state/app.state';
import { LWAReason } from '../../../admin/lwa-reasons/shared/models/left-without-assistant-reasion.model';
import { LWAReasonService } from '../../../admin/lwa-reasons/shared/services/left-without-assistant-reasons.service';
import { AssistedMember, LobbyEntryDetailEdit, LobbyEntryDetailView, LobbyInteractionDetailEdit, LobbyServiceProvidedDetailView, subtractDates, toLocalISOString } from '../../../lobby/shared/models/lobby-entry-detail-edit';
import { LobbyService } from '../../../lobby/shared/services/lobby.service';
import { ConfigurationGroup } from '../../enums/configurationGroup.enum';
import { SystemConfigurationName } from '../../enums/systemConfigurationName.enum';
import { readCastedValue } from '../../utils/read-casted-value.function';
import { EditLobbyComment } from '../../models/edit-lobby-comment';
import { ReportService } from 'src/app/report/shared/services/report.service';
import { TranslateService } from '@ngx-translate/core';
import { LocationSelectorInfo } from 'src/app/admin/user/shared/models/locationSelectorModel';
import { AbstractControl, ValidationErrors, ValidatorFn  } from '@angular/forms';
import moment from 'moment';
import { MasterLookupService } from 'src/app/master/master-services/master-lookup.service';

@Component({
  selector: 'app-lobby-detail-edit',
  templateUrl: './lobby-detail-edit.component.html',
  styleUrls: ['./lobby-detail-edit.component.scss']
})
export class LobbyDetailEditComponent implements OnInit, OnDestroy {
  isACLicenseEnabled$ = this.store.select(getACLicenseStatus);
  @Input() public lwaReasons: LWAReason[] = [];
  @Input() public dialogOpened: boolean = false;
  @Input() public dataItem: any;
  @Output() onEdited: EventEmitter<any> = new EventEmitter();
  @Output() dirtyEmitter = new EventEmitter<boolean>(false);

  public lobbyData: any;
  public lobbyView?: LobbyEntryDetailView;
  public staffMembers: StaffMember[] = [];
  public lobbyEditForm: FormGroup = new FormGroup({});
  public allServices: any;
  public isUserCanEditGrid: boolean = false;
  public lwaComment:string | null = null;
  public dateSelectionMaxLimit :Date = new Date();
  public dropdownSettings = {
    singleSelection: false,
    idField: 'id',
    textField: 'name',
    selectAllText: this.translateService.instant('_General_Select_All_Text_'),
    unSelectAllText: this.translateService.instant('_General_Unselect_All_Ts_Text_'),
    allowSearchFilter: true,
    itemsShowLimit: 1,
    searchPlaceholderText: this.translateService.instant('_General_Search_Text_')
  };
  public lobbyConfiguration: any;
  public tenantId: number = 0;

  public checkInTime?: Date | null;
  private isSyncing = false;
  public group = [{ field: "groupIndex"}];
  public allUsersForProductService: AssistedMember[] = [];
  public allActiveUsers: StaffMember[] = [];
  public gridModified = false;

  selectedLocationId: number = 0;
  selectedLocationName?: string;
  hasBranch: boolean = true;
 
  lobbyEditSubscriptions = new Subscription();
  private dateLimitIntervalSub!: Subscription;
  private selectedErrorRowMap: Map<number, any> = new Map();
  public topMostErrorRowIndex: number | null = null;

  constructor(public lobbyService: LobbyService,
    public lobbyEntryService: LobbyEntryService,
    public lookupService: MasterLookupService,
    public lwaService: LWAReasonService,
    public fb: FormBuilder,
    private store: Store<State>,private reportService:ReportService,
    private translateService: TranslateService) { }

  ngOnInit() {
    this.createForm();
    this.lobbyEditSubscriptions.add(
      this.store.select(getSystemConfigurations).subscribe(
        (configurations) => {
          const lobbyConfigurations = configurations.filter(c => c.configurationGroup === ConfigurationGroup.LobbySettings);
          if (lobbyConfigurations.length > 0) {
            this.lobbyConfiguration = {
              EnableAccountNumber: readCastedValue(lobbyConfigurations.find(ac => ac.configurationName === SystemConfigurationName.EnableAccountNumber)),
              EnablePhoneNumber: readCastedValue(lobbyConfigurations.find(ac => ac.configurationName === SystemConfigurationName.EnablePhoneNumber)),
              EnableEmailAddress: readCastedValue(lobbyConfigurations.find(ac => ac.configurationName === SystemConfigurationName.EnableEmailAddress)),
              EnableDateOfBirth: readCastedValue(lobbyConfigurations.find(ac => ac.configurationName === SystemConfigurationName.EnableDateOfBirth)),
              EnableCustomField: readCastedValue(lobbyConfigurations.find(ac => ac.configurationName === SystemConfigurationName.EnableCustomField)),
              CustomFieldText: readCastedValue(lobbyConfigurations.find(x => x.configurationName == SystemConfigurationName.CustomFieldText)),
              AccountNumberDisplayText: readCastedValue(lobbyConfigurations.find(x => x.configurationName == SystemConfigurationName.AccountNumberDisplayText)),
            };
          }
        }
      )
    );

    const tenantSubscriptions = this.store.select(getCurrentTenant).subscribe(
      (tenantInfo) => {
        if (tenantInfo !== undefined && tenantInfo !== null) {
          this.tenantId = tenantInfo.tenantID;
        }
      }
    );

    this.lobbyEditSubscriptions.add(tenantSubscriptions);
    this.isUserCanEditGrid = false;
    this.getUserPermission();
    this.reportService.getLwaReasons().subscribe((allLWAs) => {
        this.lwaReasons = allLWAs;
      })  

  }

  ngOnDestroy(): void {
    this.lobbyEditSubscriptions.unsubscribe();
    this.dateLimitIntervalSub?.unsubscribe();
  }

  onDropdownKeydown(event: KeyboardEvent) {
    if (event.key === "Enter") {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  onAccountNumberKeyUp(event: KeyboardEvent): void {
    const accountNumber: string = this.lobbyEditForm.get('accountNumber')!.value;
    if (accountNumber.includes('●')) {
      if (event.key.length === 1)
        this.lobbyEditForm.get('accountNumber')!.setValue(event.key);
      else
        this.lobbyEditForm.get('accountNumber')!.setValue('');
    }
  }

  /**
   * Save edited lobby record
   * @param event save object
   */
  public saveLobbyEntry(event: any) {
    var commentUpdateReq=false;
    const lobbyEntryEdit = new LobbyEntryDetailEdit();
    lobbyEntryEdit.AccountNumber = event.accountNumber ?? '';
    lobbyEntryEdit.DateOfBirth = event.dateOfBirth;
    lobbyEntryEdit.EmailAddress = event.email;
    lobbyEntryEdit.FirstName = event.firstName;
    lobbyEntryEdit.LastName = event.lastName;
    lobbyEntryEdit.addedTime = event.checkInTime;
    var wrapupComments=this.lobbyData.lobbyComments;
    if(wrapupComments.length>0){
      var editLobbyComment=new EditLobbyComment();
      editLobbyComment.comment = event.comments;
      editLobbyComment.commentId=wrapupComments[wrapupComments.length-1].commentId;
      commentUpdateReq=true;      
    }
    lobbyEntryEdit.Notes = event.notes;
    const selectedPersonOption = event.personRequested;
    if (!selectedPersonOption || selectedPersonOption === -1) {
      lobbyEntryEdit.UserRequested = null;
      lobbyEntryEdit.GroupRequested = null;
    } else if (selectedPersonOption.isNotificationGroup) {
      lobbyEntryEdit.GroupRequested = Number(selectedPersonOption.userId);
      lobbyEntryEdit.UserRequested = null;
    } else {
      lobbyEntryEdit.UserRequested = Number(selectedPersonOption.userId);
      lobbyEntryEdit.GroupRequested = null;
    }
    lobbyEntryEdit.PhoneNumber = event.phoneNumber;
    lobbyEntryEdit.RecieveTextMessage = event.recieveTextUpdate;
    //Commented the below line as AddedBy cannot be changed and the attribute has no usage in the backend
    //lobbyEntryEdit.AddedBy = Number(event.personRequested);
    lobbyEntryEdit.ServiceRequested = event.requests.map((obj: any) => obj.id);
    lobbyEntryEdit.PreferredLanguage = event.spanishSpeaker === true ? 3 : 1;
    lobbyEntryEdit.CustomFieldValues = { 1: `${event.customFieldValues}` };
    lobbyEntryEdit.editReason = event.editReason;
    lobbyEntryEdit.lwaReasonId = event.lwaReasonId ? Number(event.lwaReasonId) : 0;
    lobbyEntryEdit.StatusId = this.lobbyData.status;
    lobbyEntryEdit.LocationId = this.selectedLocationId;
    lobbyEntryEdit.addedTime = event.checkInTime.toISOString()
    lobbyEntryEdit.addedLocalTime = toLocalISOString(event.checkInTime); 

    const groupedMap = new Map<number, LobbyInteractionDetailEdit>();

    this.lobbyView?.servicesProvided.forEach(service => {
       if (!groupedMap.has(service.interactionId!)) {
          groupedMap.set(service.interactionId!, {
            interactionId: service.interactionId!,
            assistedBy: service.assistedBy?.userId ?? 0,
            assistTypeId: (service.waitTimeInt == 0) ? 2 : 1,
            lobbyAddedLocalTime: service.lobbyAddedTime ? toLocalISOString(service.lobbyAddedTime) : toLocalISOString(new Date()),
            assistedTime: service.serviceStartedTime ? service.serviceStartedTime.toISOString() : new Date().toISOString(),
            assistedLocalTime: service.serviceStartedTime ? toLocalISOString(service.serviceStartedTime) : toLocalISOString(new Date()),
            completedTime: service.completedLocalTime ? service.completedLocalTime.toISOString() : new Date().toISOString(),
            completedLocalTime: service.completedLocalTime ? toLocalISOString(service.completedLocalTime): toLocalISOString(new Date()),
            servicesProvided: []
          });
        }

        const interaction = groupedMap.get(service.interactionId!);
        if (interaction) {
          interaction.servicesProvided?.push({
          serviceProvidedId: service.serviceProvidedId!,
          serviceId: service.serviceId!,
          referenceNumber: service.referenceNumber,
          serviceTime: service.serviceTimeInt ?? 0,
          serviceProvidedLocalTime: service.serviceProvidedTime ? toLocalISOString(service.serviceProvidedTime): toLocalISOString(new Date()),
          serviceProvidedTime: service.serviceProvidedTime?.toISOString()
        });
      }
    });

    const interactions = Array.from(groupedMap.values());
    lobbyEntryEdit.interactions = interactions || [];

    //lobbyEntryEdit.useLocalTime = true;
    this.reportService.updateLobbyEntry(this.lobbyData.lobbyEntryId, lobbyEntryEdit).subscribe((data) => {
      this.dialogOpened = false;
      this.allServices = [];
      if(commentUpdateReq){
        this.lobbyEntryService.updateLobbyComment(this.lobbyData.lobbyEntryId,editLobbyComment).subscribe((data) => {
        });
      }
    
      this.onEdited.emit();
    }, error => {
      console.error(error);
    })
  }

  /**
   * Clear values and close edit popup
   */
  public close() {
    this.dialogOpened = false;
    this.lobbyData = null;
    this.gridModified = false;
    this.lobbyEditForm.reset();
  }

  /**
   * Fetch editing lobby entry record
   * and relevent information and imitate value patching
   */
  public open() {
    this.isUserCanEditGrid = false;
    this.getUserPermission();
    this.lobbyEntryService.getLobbyEntryDetails(this.dataItem.lobbyEntryId).subscribe((lobbyData) => {
     
      this.createForm();
      this.lobbyView = new LobbyEntryDetailView(lobbyData);
      if(this.lobbyView!=null && this.lobbyView.completedLocalTime!=null){
        this.lobbyView.completedLocalTime=this.lobbyView.completedLocalTime;
      }      
      this.lobbyData = lobbyData;
      this.getAllStaffMembers();
      this.getAllUsersForProductService();
      this.getAllServices(lobbyData.locationId);
      
      this.getUserPermission();
      if (this.lobbyData) {
        lobbyData.lobbyStatus = (lobbyData.lobbyStatus === 'Completed' ? this.translateService.instant('_General_Completed_Text_') : (this.lobbyData.lobbyStatus === 'No Show' ? this.translateService.instant('_General_No_Show_Text_') : this.translateService.instant('_General_LWA_Abbr_Text_')));
      }
      if (this.lobbyData && this.lobbyData.utcoffsetInMinutes != null) {
        this.updateDateSelectionLimit(this.lobbyData.utcoffsetInMinutes);
        // Refresh the limit every 30 seconds
        this.dateLimitIntervalSub = interval(30000).subscribe(() => {
          if (this.lobbyData && this.lobbyData?.utcoffsetInMinutes != null) {
            this.updateDateSelectionLimit(this.lobbyData.utcoffsetInMinutes);
          }
        });
      }
      
      this.selectedLocationName = this.lobbyData.locationName;
      this.selectedLocationId = this.lobbyData.locationId;
      this.checkInTime = new Date(this.lobbyData.addedLocalTime.split('.')[0]);
      this.assignGroupIndices();
      this.updateCompletedTimeValidation();
      this.dialogOpened = true;
    })
    
  }
 /**
   * Get user permissions to enable/disable save button
  */
 private getUserPermission() {
  this.store.select(GetUserPermissionList).subscribe((allUserPermissions) => {
    if (allUserPermissions) {
      this.isUserCanEditGrid = allUserPermissions.includes(UserPermissions['Edit Lobby Editor Report']);
      if (this.isUserCanEditGrid) {
        this.lobbyEditForm.enable();
      } else {
        this.lobbyEditForm.disable();
      }
    }
  });
}
  /**
   * Fetch all services from store
   */
  public getAllServices(locationId: number) {
    this.lobbyService.getAllServices(locationId).subscribe((services) => {
      const totalServices = Object.values(services);
      this.allServices = totalServices
        .map((s: any) => ({
          id: s.service.id,
          name: s.service.name,
          categoryName: s.categoryName
        }))
        .sort((a: any, b: any) => a.name.localeCompare(b.name)); // <-- Sort alphabetically by name
    this.lobbyView?.setServices(this.allServices);
  });
  }


  /**
   * Get all staff members from store
   */
  public getAllStaffMembers() {
    this.staffMembers = [];
    this.allActiveUsers = [];

    // Get users
    this.lobbyService.getAllUsers(true).subscribe((allStaffMembers) => {
      this.allActiveUsers = [...allStaffMembers];
      // Filter and collect users (excluding system/kronos users)
      const users: StaffMember[] = [];
      allStaffMembers.forEach(l => {
        if (l.userType != 1) {
          if ((l.userType == 2 && this.tenantId == 1) || l.userType == 3) {
            users.push(l);
          }
        }
      });

      // Add assisted users from interactions if not already present
      this.lobbyData.lobbyInteractions.forEach((o: any) => {
        if (users.find(s => s.userId == o.assistedBy.userId) != null) {
          return;
        }
        const staffMember: StaffMember = {} as StaffMember;
        staffMember.userId = o.assistedBy.userId;
        staffMember.firstName = o.assistedBy.firstName;
        staffMember.lastName = o.assistedBy.lastName;
        users.push(staffMember);
      });

      // Sort users alphabetically by firstName
      users.sort((a, b) => a.firstName.localeCompare(b.firstName));

      // Get notification groups, sort by name, and add to staffMembers
      this.lookupService.getNotificationGroups().subscribe((notificationGroups: any[]) => {
        const sortedGroups = [...notificationGroups].sort((a, b) => a.name.localeCompare(b.name));
        const notificationStaffMembers: StaffMember[] = sortedGroups.map(group => ({
          userId: group.id,
          firstName: group.name,
          lastName: '',
          isNotificationGroup: true,
          isSpanishSpeaker: false,
          userType: 0
        }));

        // Combine users first, then notification groups
        this.staffMembers = [...users, ...notificationStaffMembers];
        this.setValuesToLobbyEditForm();
      });
    });
  }
  
  public getAllUsersForProductService() {
    this.allUsersForProductService=[];
    this.lobbyService.getAllUsersForProductService().subscribe((allUsers) => {
      if(allUsers){
        this.allUsersForProductService = [...allUsers]
        
        this.lobbyData.lobbyInteractions.forEach((o: any) => {
          if (this.allUsersForProductService.find(s => s.userId == o.assistedBy.userId) != null) {         
            return;
          }
          const staffMember: AssistedMember = {} as AssistedMember;
          
          staffMember.userId = o.assistedBy.userId;
          staffMember.firstName = o.assistedBy.firstName;
          staffMember.lastName = o.assistedBy.lastName;

          if(this.allActiveUsers?.find(s => s.userId == o.assistedBy.userId)!= null ){
            this.allUsersForProductService.push(staffMember)
          }

        });
        this.allUsersForProductService.sort((a: any, b: any) => a.firstName.localeCompare(b.firstName))
      }
      
    })
  }

  public isUserInSelectableList(userId: number): boolean {
    return this.allUsersForProductService.some(u => u.userId === userId);
  }


  /**
   * Create form with minimum validation in checkin
   */
  public createForm() {
    this.lobbyEditForm = this.fb.group({
      accountNumber: [null, [Validators.pattern(/^[^<>&]*$/)]],
      firstName: [null, [Validators.required, Validators.pattern(/^[^<>&]*$/)]],
      lastName: [null, [Validators.required, Validators.pattern(/^[^<>&]*$/)]],
      notes: [null, [Validators.pattern(/^[^<>&]*$/)]],
      dateOfBirth: [null, []],
      checkInTime: [null, [Validators.required]],
      completedTime: [null,[Validators.required]],
      phoneNumber: [[], []],
      recieveTextUpdate: [null, []],
      email: [null, [Validators.pattern(new RegExp('^(([a-zA-Z0-9_\\-\\.]+)@((\\[[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}\\.)|(([a-zA-Z0-9\\-]+\\.)+))([a-zA-Z]{2,4}|[0-9]{1,3})(\\]?)(\s*(;|,)\s*|\s*$))*$'))]],
      location: [null, []],
      lobbyStatus: [null, []],
      lwaReasonId: [null, []],
      personRequested: [null, []],
      spanishSpeaker: [null, []],
      requests: [null, []],
      comments: [null, [Validators.pattern(/^[^<>&]*$/)]],//notes
      customFieldValues: [null, [Validators.pattern(/^[^<>&]*$/)]],
      editReason: [null, [Validators.required, Validators.pattern(/^[^<>&]*$/)]]
      }, {
      validators: [this.checkInBeforeCompletedValidator()]
    });

    if (this.isUserCanEditGrid) {
      this.lobbyEditForm.enable();
    } else {
      this.lobbyEditForm.disable();
    }
  }


  /**
   * Patch fetched values to the Form
   */
  public setValuesToLobbyEditForm() {
    var comments='';
    var wrapupComments=this.lobbyData.lobbyComments.filter((x: any)=>x.isLWA!=true);
    if(wrapupComments.length>0){
      comments =wrapupComments[wrapupComments.length-1].comment;
  }

  this.lobbyEditForm.patchValue({
    dateOfBirth: this.lobbyData.dateOfBirth,
    checkInTime: this.lobbyView?.checkInTime,
      completedTime:this.lobbyView?.completedLocalTime,
    recieveTextUpdate: this.lobbyData.recieveTextMessage,
    phoneNumber: this.lobbyData.phoneNumber,
    spanishSpeaker: this.lobbyData.preferredLanguage.languageId === 1 ? false : true,
    accountNumber: this.lobbyData.accountNumber,
    firstName: this.lobbyData.firstName,
    lastName: this.lobbyData.lastName,
    email: this.lobbyData.emailAddress,
    notes: this.lobbyData.notes,
    comments: comments,
    customFieldValues: (this.lobbyData.customFieldValues.length > 0) ? this.lobbyData.customFieldValues[0].customFieldValue : '',
    lobbyStatus: this.lobbyData.status,
  });

  // Patch the dropdown with the correct StaffMember object
  let selectedStaffMember: StaffMember | null = null;
  if (this.lobbyData.userRequested) {
    selectedStaffMember = this.staffMembers.find(
      s => s.userId === this.lobbyData.userRequested.userId && !s.isNotificationGroup
    ) ?? null;
  } else if (this.lobbyData.groupRequested) {
    selectedStaffMember = this.staffMembers.find(
      s => s.userId === this.lobbyData.groupRequested.userId && s.isNotificationGroup
    ) ?? null;
  }

  this.lobbyEditForm.patchValue({
    personRequested: selectedStaffMember
  });

  if (this.lobbyData.requestedServices) {
    this.lobbyEditForm.patchValue({
      requests: this.lobbyData.requestedServices
    });
  }

    if(this.lobbyData && this.lobbyData.lwaReasonId){
    const reasonExists = this.lwaReasons.some(r => r.id === this.lobbyData.lwaReasonId);
    this.lobbyEditForm.patchValue({
        lwaReasonId: reasonExists ? this.lobbyData.lwaReasonId: this.lwaReasons[0].id
    });
  }
  this.lobbyEditForm.updateValueAndValidity();
}

  EnableReceiveTextMessageCheckBox() {
    if (this.lobbyEditForm?.controls.phoneNumber.invalid || this.lobbyEditForm?.controls.phoneNumber.pristine ||
      this.lobbyEditForm?.controls.phoneNumber.value === '') {
      this.lobbyEditForm.controls.recieveTextUpdate.disable();
      this.lobbyEditForm.controls.recieveTextUpdate.setValue(false);
    } else {
      this.lobbyEditForm.controls.recieveTextUpdate.enable();
    }
  }

  /**
   * Checks for invalid form controls
   * @returns error controls
   */
  public findInvalidControls() {
    const invalid = [];
    const controls = this.lobbyEditForm.controls;
    for (const name in controls) {
      if (controls[name].invalid) {
        invalid.push(name);
      }
    }
    return invalid;
  }

  public addCommand({ sender, rowIndex, dataItem }: AddEvent) { }

  public editCommand({ sender, rowIndex, dataItem }: EditEvent) {
    sender.editRow(rowIndex);
  }

  public removeCommand(removeEvent: RemoveEvent) { }

  onLocationChange(locationData: LocationSelectorInfo) {
      this.dirtyEmitter.emit(true);
      if(locationData.id == undefined || locationData.id == null){
        throw Error("locationData.id was undefined or null! Which was not expected!");
      } 
 
      this.selectedLocationId = locationData.id;
      this.selectedLocationName = locationData.locationName;
      
    }

  private ConvertLocalTimeToUtc() {
    var refreshDate = new Date();
    const utcTime = {
      year: refreshDate.getUTCFullYear(),
      month: refreshDate.getUTCMonth(),
      day: refreshDate.getUTCDate(),
      hours: refreshDate.getUTCHours(),
      minutes: refreshDate.getUTCMinutes(),
      seconds: refreshDate.getUTCSeconds(),
      milliseconds: refreshDate.getUTCMilliseconds()
    };

    return new Date(utcTime.year, utcTime.month, utcTime.day, utcTime.hours, utcTime.minutes, utcTime.seconds, utcTime.milliseconds);
  }

  updateDateSelectionLimit(utcoffsetInMinutes: number): void {
    var nowUtc = this.ConvertLocalTimeToUtc()
    var nowWithOffset = new Date();
    nowWithOffset.setTime(nowUtc.getTime() + (utcoffsetInMinutes * 60 * 1000));
    var dateAfterMaxLimit = new Date();

    dateAfterMaxLimit.setTime(nowWithOffset.getTime());
    this.dateSelectionMaxLimit = dateAfterMaxLimit;
  }

    onCheckInTimeChange(value: Date): void {
      if (this.isSyncing) return;
      this.isSyncing = true;

      if (this.checkInTime && this.lobbyView?.servicesProvided?.length && value) {

        const timeDiff = value.getTime() - this.checkInTime.getTime();
        if(timeDiff !== 0){
          this.lobbyView.servicesProvided.forEach(sp => {
            if (sp.serviceStartedTime) {
              sp.serviceStartedTime = new Date(sp.serviceStartedTime.getTime() + timeDiff);
            }
            if (sp.completedLocalTime) {
              sp.completedLocalTime = new Date(sp.completedLocalTime.getTime() + timeDiff);
            }
            if (sp.serviceProvidedTime) {
              sp.serviceProvidedTime = new Date(sp.serviceProvidedTime.getTime() + timeDiff);
            }
            if(sp.lobbyAddedTime){
              sp.lobbyAddedTime =  new Date(sp.lobbyAddedTime.getTime() + timeDiff);
            }
            if(sp.lobbyAssistedTime){
              sp.lobbyAssistedTime = new Date(sp.lobbyAssistedTime.getTime() + timeDiff);
            }
            });
            if(this.lobbyView?.completedLocalTime){
              this.lobbyView.completedLocalTime = new Date(this.lobbyView?.completedLocalTime.getTime() + timeDiff); 
            }
            this.lobbyEditForm.get('completedTime')?.setValue(this.lobbyView.completedLocalTime);
        }
      }

      this.checkInTime = new Date(value);
      this.isSyncing = false;
    }

    onCompletedTimeChange(value: Date): void {
      if (this.isSyncing) return;
      this.isSyncing = true;
      if (this.lobbyView?.completedLocalTime && this.lobbyView?.servicesProvided?.length && value) {
        const timeDiff = value.getTime() - this.lobbyView.completedLocalTime.getTime();
        if(timeDiff !== 0){
          if(this.checkInTime){
            const newCheckinTime = new Date(this.checkInTime.getTime() + timeDiff);
            this.lobbyEditForm.get('checkInTime')?.setValue(newCheckinTime);
            this.checkInTime = newCheckinTime;
          }
          
          this.lobbyView.servicesProvided.forEach(sp => {
            if (sp.serviceStartedTime) {
              sp.serviceStartedTime = new Date(sp.serviceStartedTime.getTime() + timeDiff);
            }
            if (sp.completedLocalTime) {
              sp.completedLocalTime = new Date(sp.completedLocalTime.getTime() + timeDiff);
            }
            if (sp.serviceProvidedTime) {
              sp.serviceProvidedTime = new Date(sp.serviceProvidedTime.getTime() + timeDiff);
            }
            if(sp.lobbyAddedTime){
            sp.lobbyAddedTime =  new Date(sp.lobbyAddedTime.getTime() + timeDiff);
            }
            if(sp.lobbyAssistedTime){
              sp.lobbyAssistedTime = new Date(sp.lobbyAssistedTime.getTime() + timeDiff);
            }
          });

            this.lobbyView.completedLocalTime = new Date(value);
        }
      }
       this.isSyncing = false;
    }

    checkInBeforeCompletedValidator(): ValidatorFn {
      return (group: AbstractControl): ValidationErrors | null => {
        const checkInTime = group.get('checkInTime')?.value;
        const completedTime = group.get('completedTime')?.value;
    
        if (!checkInTime || !completedTime) return null;
    
        if (checkInTime >= completedTime) {
          return { checkInAfterCompleted: true };
        }
    
        return null;
      };
    }

    updateCompletedTimeValidation() {
      const completedTimeControl = this.lobbyEditForm.get('completedTime');
      if (this.lobbyData?.status !== 6) {
        completedTimeControl?.setValidators([Validators.required]);
      } else {
        completedTimeControl?.clearValidators();
      }
      completedTimeControl?.updateValueAndValidity();
    }

    onProductServiceChange(dataItem: any,index: number){
      this.gridModified = true;
      const selectedService = dataItem.allServices.find((s: any) => s.id === dataItem.serviceId);
      
      if (this.lobbyView && this.lobbyView.servicesProvided?.[index]) {
        this.lobbyView.servicesProvided[index].serviceCategoryName = selectedService?.categoryName || '';
        this.lobbyView.servicesProvided[index] .serviceId = selectedService?.id;
        this.lobbyView.servicesProvided[index].serviceName = selectedService?.name;
      }
    }

    addProduct(): void {
      const existingNegatives = this.lobbyView?.servicesProvided.map(s => s.serviceProvidedId)
        .filter((id): id is number => typeof id === 'number' && id < 0) || [];

      const nextServiceId = existingNegatives.length > 0
          ? Math.min(...existingNegatives) - 1
          : -1;

      const newEntry = new LobbyServiceProvidedDetailView();
      newEntry.serviceId = null;
      newEntry.serviceName = '';
      newEntry.serviceCategoryName = '';
      newEntry.referenceNumber = '';
      newEntry.assistedBy= {
                            userId: null,
                            firstName: '',
                            lastName: '',
                            isSpanishSpeaker: false,
                            showInKiosk: false,
                            showInLobby: false,
                          };
      newEntry.waitTime = '';
      newEntry.serviceTime = '';
      newEntry.wrapUpTime = '';
      newEntry.allServices = this.allServices;
      newEntry.assistedByName = '';
      newEntry.serviceProvidedId = nextServiceId;
      newEntry.interactionId = nextServiceId;

      //CXDT-2314 - #1 The new interaction's assist started time should be the previous one's completed time 
      if (this.lobbyView?.servicesProvided?.length) {
        const prev = this.lobbyView.servicesProvided[this.lobbyView.servicesProvided.length - 1];
        if (prev.completedLocalTime instanceof Date) {
          newEntry.serviceStartedTime = new Date(prev.completedLocalTime.getTime());
          newEntry.completedLocalTime = new Date(prev.completedLocalTime.getTime());
        }
      }

      if (this.lobbyView) {
        this.lobbyView.servicesProvided = [
          ...(this.lobbyView.servicesProvided || []),
          newEntry
        ];
      }

      if(this.lobbyData.status !==5){
        this.lobbyData.status = 5;
        this.lobbyData.lobbyStatus = this.translateService.instant('_General_Completed_Text_');
        this.updateCompletedTimeValidation();
      }
      this.assignGroupIndices();
    }

    onAssistedByChange(dataItem: any){
      this.gridModified = true;
        const assistedUser = this.allUsersForProductService.find(o=> o.userId == dataItem.assistedBy.userId);
        if(assistedUser){
          this.lobbyView?.servicesProvided.filter(sp=>sp.interactionId == dataItem.interactionId).forEach(sp=>{
            if(sp.assistedBy){
              sp.assistedBy.userId = assistedUser.userId;
              sp.assistedBy.firstName = assistedUser.firstName;
              sp.assistedBy.lastName = assistedUser.lastName;
              sp.assistedBy.isSpanishSpeaker = assistedUser.isSpanishSpeaker;
              sp.assistedBy.showInKiosk = assistedUser.showInKiosk;
              sp.assistedBy.showInLobby = assistedUser.showInLobby;
              sp.assistedByName = assistedUser.firstName + ' ' + assistedUser.lastName;
            }
            
          })
        }

    }

    openProductDelete(index: number): void {
      const services = this.lobbyView?.servicesProvided;
      if (!services || services.length <= 1) return;
      const removedItem = services[index];
      const interactionId = removedItem.interactionId;

      services.splice(index, 1);
      this.assignGroupIndices();

        // --- CXDT-2314 - #8 ---
      // Only if there is a service below and above the deleted one
      if (index > 0 && index < services.length) {
         const above = services[index - 1];
         const below = services[index];
          if (above?.completedLocalTime instanceof Date && below) {
            below.serviceStartedTime = new Date(above.completedLocalTime.getTime());
          }
      }

      const remainingServices = services.filter(sp => sp.interactionId === interactionId);
      if (remainingServices.length) {
          // Sort by serviceStartedTime
          const sortedByStart = [...remainingServices]
            .filter(sp => sp.serviceStartedTime instanceof Date)
            .sort((a, b) => (a.serviceStartedTime as Date).getTime() - (b.serviceStartedTime as Date).getTime());

          const firstService = sortedByStart[0];
          if(firstService.lobbyAssistedTime instanceof Date && firstService.serviceStartedTime instanceof Date &&
              firstService.lobbyAssistedTime.getTime() !== firstService.serviceStartedTime.getTime()){
              const newLobbyAssistedTime = firstService?.serviceStartedTime;
              let newLobbyAddedTime: Date | undefined;
              if (newLobbyAssistedTime && typeof firstService.waitTimeInt === 'number') {
                newLobbyAddedTime = new Date(newLobbyAssistedTime.getTime() - firstService.waitTimeInt * 1000);
              }

              for (const sp of remainingServices) {
                  sp.lobbyAssistedTime = newLobbyAssistedTime;
                  sp.lobbyAddedTime = newLobbyAddedTime;
                }
              }
          

          const sortedByProvided = [...remainingServices]
            .filter(sp => sp.serviceProvidedTime instanceof Date && sp.wrapUpTimeInt)
            .sort((a, b) => (b.serviceProvidedTime as Date).getTime() - (a.serviceProvidedTime as Date).getTime());

          const lastService = sortedByProvided[0];
          if(lastService.serviceProvidedTime instanceof Date && lastService.completedLocalTime instanceof Date && lastService.wrapUpTimeInt &&
              lastService.completedLocalTime.getTime() !== (lastService.serviceProvidedTime.getTime()+ lastService.wrapUpTimeInt*1000)){
              let newCompletedTime: Date | undefined;
              if (lastService?.serviceProvidedTime instanceof Date && typeof lastService.wrapUpTimeInt === 'number') {
                newCompletedTime = new Date(lastService.serviceProvidedTime.getTime() + lastService.wrapUpTimeInt * 1000);
              }

              for (const sp of remainingServices) {
                  sp.completedLocalTime = newCompletedTime;
                }
              }
        }
     
        const allLobbyAddedTimes = services.filter(sp => sp.lobbyAddedTime instanceof Date).map(sp => sp.lobbyAddedTime as Date);

        const newCheckinTime = allLobbyAddedTimes.length ? new Date(Math.min(...allLobbyAddedTimes.map(d => d.getTime()))) : undefined;
          if (newCheckinTime && this.checkInTime && newCheckinTime.getTime() !== this.checkInTime.getTime()) {
            this.checkInTime = newCheckinTime
            this.lobbyEditForm.get('checkInTime')?.setValue(newCheckinTime);
          }

          const allLobbyCompletedTime= services.filter(sp => sp.completedLocalTime instanceof Date).map(sp => sp.completedLocalTime as Date);
          const higherCompletedTimeValue = allLobbyCompletedTime.length ? new Date(Math.min(...allLobbyCompletedTime.map(d => d.getTime()))) : undefined;
          if (higherCompletedTimeValue && this.lobbyView && 
            (!this.lobbyView.completedLocalTime || this.lobbyView.completedLocalTime.getTime() !== higherCompletedTimeValue.getTime()) ) 
            {   
                this.lobbyView.completedLocalTime = higherCompletedTimeValue;
                this.lobbyEditForm.get('completedTime')?.setValue(higherCompletedTimeValue);
            }
    }

    onMaskedBlur(item: any,maskedBoxRef: any): void {
      const rawValue = (maskedBoxRef as any)?.maskedValue;
      this.validateField(item, 'waitTime', rawValue);
    }

  validateField(item: any, field: string, rawValue: string = ''): void {
    if (!item.errors) item.errors = {};

    const allRows = this.lobbyView?.servicesProvided || [];
    const currentIndex = allRows.findIndex(row => row === item);

    switch (field) {
      case 'serviceId':
        this.gridModified = true;
        item.errors.serviceId = !item.serviceId ? this.translateService.instant('_Report_Lobby_Product_Service_Required_Error_Msg_') : '';
        break;
      case 'assistedBy':
        this.gridModified = true;
        item.errors.assistedBy = !item.assistedBy.userId ? this.translateService.instant('_Report_Lobby_Assisted_By_Required_Error_Msg_') : '';
        break;
      case 'serviceStartedTime':
        this.gridModified = true;
        item.errors.serviceStartedTime = !item.serviceStartedTime ? this.translateService.instant('_Report_Lobby_Assisted_Started_Required_Error_Msg_') : '';
        item.errors.assistStartedError = '';

          // Find the previous interaction (by order in servicesProvided)
        const allRows = this.lobbyView?.servicesProvided || [];
        const currentIndex = allRows.findIndex(row => row === item);
        var continueCalculation = true;
        if (item.serviceStartedTime && this.checkInTime) {
          const serviceStart = new Date(item.serviceStartedTime);

          serviceStart.setFullYear(this.checkInTime.getFullYear());
          serviceStart.setMonth(this.checkInTime.getMonth());
          serviceStart.setDate(this.checkInTime.getDate());

          item.serviceStartedTime = serviceStart;
          // CXDT-2314 - #2 Validation: For second and subsequent interactions
          if (currentIndex > 0) {
            const prevItem = allRows[currentIndex - 1];
            if (prevItem && prevItem.completedLocalTime instanceof Date) {
              // Do not show error if current item is in the first interaction
              // or if current and previous are in the same interaction
              const isFirstInteraction = item.interactionId === allRows[0].interactionId;
              const isSameInteractionAsPrev = item.interactionId === prevItem.interactionId;
              if (!isFirstInteraction && !isSameInteractionAsPrev && item.serviceStartedTime < prevItem.completedLocalTime) {
                item.errors.assistStartedError =
                  this.translateService.instant('_Report_Lobby_Assist_Started_Must_Be_After_Previous_Completed_Error_Msg_');
                  continueCalculation = false;
              } else if (isSameInteractionAsPrev) {
                // assist started time cannot go below (current assist started time - current wait time) - This only applies to records in the current interaction
                if (item.serviceStartedTime && typeof item.waitTimeInt === 'number') {
                  const previousAssistStartedTime = item.serviceProvidedTime.getTime() - item.serviceTimeInt * 1000;
                  const minAllowedTime = new Date(previousAssistStartedTime - item.waitTimeInt * 1000);
                  if (item.serviceStartedTime < minAllowedTime) {
                    item.errors.assistStartedError =
                      this.translateService.instant('_Report_Lobby_Assist_Started_Must_Be_After_Previous_Completed_Error_Msg_');
                      continueCalculation = false;
                  }
                }
              }
            }
          }
          const checkInTimeValue = new Date(this.lobbyEditForm.get('checkInTime')?.value);
          const completedTimeRaw = this.lobbyEditForm.get('completedTime')?.value;
          const completedTimeValue = completedTimeRaw ? new Date(completedTimeRaw) : null;
          if ((checkInTimeValue <= item.serviceStartedTime) || (!completedTimeValue && item.interactionId < 0)) {
            if (item.serviceTime && item.serviceProvidedTime) {
              const previousAssistStartedTime = item.serviceProvidedTime.getTime() - item.serviceTimeInt * 1000;
              const timeDiff = item.serviceStartedTime.getTime() - previousAssistStartedTime;

              if (timeDiff !== 0 && continueCalculation) {
                const previousCompletedTime = item.completedLocalTime
                this.updateServiceTimes(item, timeDiff);
                this.validateAndUpdateCompleteTime(previousCompletedTime, item);
                this.updateAssistTimes(item);
              }

            } else if (!item.serviceProvidedTime && item.interactionId < 0) {
              if (item.serviceTimeInt) {
                item.serviceProvidedTime = new Date(item.serviceStartedTime.getTime() + item.serviceTimeInt * 1000)

                if (item.wrapUpTimeInt) {
                  item.completedLocalTime = new Date(item.serviceProvidedTime.getTime() + item.wrapUpTimeInt * 1000)
                  this.updateCompletedTimeIfLater(item.completedLocalTime, item);
                  this.updateAssistTimes(item);
                }
              }
              item.lobbyAssistedTime = item.serviceStartedTime;
              if (item.waitTimeInt) {
                item.lobbyAddedTime = new Date(item.serviceStartedTime.getTime() - (item.waitTimeInt * 1000))
                if (this.lobbyView?.servicesProvided.length == 1) {
                  this.checkInTime = item.lobbyAddedTime
                  this.lobbyEditForm.get('checkInTime')?.setValue(item.lobbyAddedTime);
                  this.validateServiceStartedTime(item);
                }
                else if (item.lobbyAddedTime < this.checkInTime) {
                  this.checkInTime = item.lobbyAddedTime
                  this.lobbyEditForm.get('checkInTime')?.setValue(item.lobbyAddedTime);
                  this.validateServiceStartedTime(item);
                }
              }
            }
          }
          else if (currentIndex === 0) {
            item.errors.assistStartedError = this.translateService.instant('_Report_Lobby_Assist_Time_Invalid_Error_Msg_');
          }
        }
        break;
      case 'waitTime':
        this.gridModified = true;
        if(rawValue == '00:00:00'){
          item.waitTime = '00:00:00';
          item.waitTimeInt = 0
        }

        const checkFirstInteraction = this.lobbyView?.servicesProvided[0].interactionId === item.interactionId;
        let hasError = false;
        if (checkFirstInteraction && item.waitTimeInt == null) {
          hasError = true;
        } else if (!checkFirstInteraction && !item.waitTimeInt) {
          hasError = true;
        }

        if (hasError) {
          item.errors.waitTime = this.translateService.instant('_Report_Lobby_Wait_Time_Required_Error_Msg_');
          const previousWaitTime = subtractDates(item.lobbyAssistedTime, item.lobbyAddedTime)
          const timeDiff = item.waitTimeInt - previousWaitTime;
          const matchedServices = this.lobbyView?.servicesProvided.filter(sp => sp.interactionId === item.interactionId) || [];
          this.adjustTimes(matchedServices, timeDiff, item.waitTime);

        } else {
          item.errors.waitTime = '';
        }

        if (!item.errors?.waitTime) {
          const matchedServices = this.lobbyView?.servicesProvided.filter(sp => sp.interactionId === item.interactionId) || [];
          if (item.lobbyAddedTime instanceof Date && item.lobbyAssistedTime instanceof Date) {
            const previousWaitTime = subtractDates(item.lobbyAssistedTime, item.lobbyAddedTime)
            const timeDiff = item.waitTimeInt - previousWaitTime;

            if (timeDiff !== 0) {
              const previousCompletedTime = item.completedLocalTime
              this.adjustTimes(matchedServices, timeDiff, item.waitTime);
              this.validateAndUpdateCompleteTime(previousCompletedTime, item)
              this.updateAssistTimes(item);
            }
          } else {
            if (item.serviceStartedTime) {
              // Update serviceStartedTime by adding/subtracting the timeDiff
              if (item.serviceStartedTime instanceof Date) {
                if (item.waitTimeInt !== 0) {
                  item.serviceStartedTime = new Date(item.serviceStartedTime.getTime() + item.waitTimeInt * 1000);
                }
              }
              item.lobbyAssistedTime = item.serviceStartedTime;
              item.lobbyAddedTime = new Date(item.serviceStartedTime.getTime() - (item.waitTimeInt * 1000));
              if (this.lobbyView?.servicesProvided.length == 1) {
                this.checkInTime = item.lobbyAddedTime
                this.lobbyEditForm.get('checkInTime')?.setValue(item.lobbyAddedTime);
                this.validateServiceStartedTime(item);
              }
              else if (this.checkInTime && item.lobbyAddedTime < this.checkInTime) {
                this.checkInTime = item.lobbyAddedTime
                this.lobbyEditForm.get('checkInTime')?.setValue(item.lobbyAddedTime);
                this.validateServiceStartedTime(item);
              }
            }
          }
        }
        break;
      case 'serviceTime':
        this.gridModified = true;
        item.errors.serviceTime = !item.serviceTimeInt ? this.translateService.instant('_Report_Lobby_Assist_Time_Required_Error_Msg_') : '';
        if (!item.serviceTimeInt || !item.serviceStartedTime) break;
        if (item.serviceProvidedTime) {
          const previousServiceTime = subtractDates(item.serviceProvidedTime, item.serviceStartedTime)
          const timeDiff = item.serviceTimeInt - previousServiceTime;
          if (timeDiff !== 0) {
            item.serviceProvidedTime = new Date(item.serviceProvidedTime.getTime() + timeDiff * 1000);
            if (item.wrapUpTimeInt) {
              const cTime = (item.serviceProvidedTime.getTime() + item.wrapUpTimeInt * 1000);
              const previousCompletedTime = item.completedLocalTime;
              const matchedService = this.lobbyView?.servicesProvided.find(sp => sp.interactionId === item.interactionId && sp.serviceProvidedId !== item.serviceProvidedId);
              if(matchedService){
                this.updateSubsequentServices(item, previousCompletedTime, timeDiff)
              }else{
                item.completedLocalTime = new Date(cTime);
                this.validateAndUpdateCompleteTime(previousCompletedTime, item)
                this.updateAssistTimes(item);
              }
              
            }else {
                const matchedService = this.lobbyView?.servicesProvided.find(sp => sp.interactionId === item.interactionId && sp.serviceProvidedId !== item.serviceProvidedId);
                if (matchedService && matchedService.wrapUpTimeInt) {
                  const cTime = (item.serviceProvidedTime.getTime() + matchedService.wrapUpTimeInt * 1000);
                  if (matchedService.completedLocalTime && matchedService.completedLocalTime instanceof Date) {
                    if (cTime > matchedService.completedLocalTime.getTime()) {
                      this.lobbyView?.servicesProvided.filter(sp => sp.interactionId === item.interactionId).forEach(sp => {
                        sp.completedLocalTime = new Date(cTime);
                      })
                      this.updateCompletedTimeIfLater(cTime, item);
                      this.updateAssistTimes(item);
                    }
                  }
                }
            }
          }
        }
        else {
          item.serviceProvidedTime = new Date(item.serviceStartedTime.getTime() + item.serviceTimeInt * 1000);
          if (item.wrapUpTimeInt) {
            item.completedLocalTime = new Date(item.serviceProvidedTime.getTime() + item.wrapUpTimeInt * 1000);
            this.updateCompletedTimeIfLater(item.completedLocalTime, item);
            this.updateAssistTimes(item);
          }
        }
        break;
      case 'wrapUpTime':
        this.gridModified = true;
        item.errors.wrapUpTime = !item.wrapUpTimeInt ? this.translateService.instant('_Report_Lobby_Wrap_Up_Time_Required_Error_Msg_') : '';
        if (!item.wrapUpTimeInt) break;
        const getMatchedService = () =>
          this.lobbyView?.servicesProvided.find(
            sp => sp.interactionId === item.interactionId && sp.serviceProvidedId !== item.serviceProvidedId
          );

          const matchedService = getMatchedService();
          if (matchedService && matchedService?.wrapUpTimeInt) {
            if (matchedService?.wrapUpTimeInt !== item.wrapUpTimeInt) {
              const timeDiff = item.wrapUpTimeInt - matchedService.wrapUpTimeInt;
              let previousCompletedTime = new Date();
              this.lobbyView?.servicesProvided.filter(sp => sp.interactionId === item.interactionId).forEach(sp => {
                sp.wrapUpTime = item.wrapUpTime;
                if (sp.completedLocalTime && sp.completedLocalTime instanceof Date) {
                  previousCompletedTime = sp.completedLocalTime;
                  sp.completedLocalTime = new Date(sp.completedLocalTime.getTime() + timeDiff * 1000);
                }
              })
              this.validateAndUpdateCompleteTime(previousCompletedTime, item)
              this.updateAssistTimes(item);
            }
          }
          else {
            if (item.serviceProvidedTime instanceof Date) {
              if (item.completedLocalTime instanceof Date) {
                const previousWrapUpTime = subtractDates(item.completedLocalTime, item.serviceProvidedTime);
                const timeDiff = item.wrapUpTimeInt - previousWrapUpTime;
                if (timeDiff !== 0) {
                  const previousCompletedTime = item.completedLocalTime;
                  item.completedLocalTime = new Date(item.serviceProvidedTime.getTime() + item.wrapUpTimeInt * 1000);
                  this.validateAndUpdateCompleteTime(previousCompletedTime, item)
                  this.updateAssistTimes(item);
                }
              } else {
                item.completedLocalTime = new Date(item.serviceProvidedTime.getTime() + item.wrapUpTimeInt * 1000);
                this.updateCompletedTimeIfLater(item.completedLocalTime, item);
                this.updateAssistTimes(item);
              }
            } else if (item.completedLocalTime instanceof Date) {
              item.serviceProvidedTime = new Date(item.completedLocalTime.getTime() - item.wrapUpTimeInt * 1000);
              if (item.serviceTimeInt) {
                item.serviceStartedTime = new Date(item.serviceProvidedTime.getTime() - item.serviceTimeInt * 1000);
                item.lobbyAssistedTime = item.serviceStartedTime
                if (item.waitTimeInt) {
                  item.lobbyAddedTime = new Date(item.serviceStartedTime.getTime() - item.waitTimeInt * 1000);
                  if (this.lobbyView?.servicesProvided.length == 1) {
                    this.checkInTime = item.lobbyAddedTime
                    this.lobbyEditForm.get('checkInTime')?.setValue(item.lobbyAddedTime);
                    this.validateServiceStartedTime(item);
                  }
                  else if (this.checkInTime && item.lobbyAddedTime < this.checkInTime) {
                    this.checkInTime = item.lobbyAddedTime
                    this.lobbyEditForm.get('checkInTime')?.setValue(item.lobbyAddedTime);
                    this.validateServiceStartedTime(item);
                  }
                }
              }
            }
          }
        break;
    }
    const hasErrors = Object.values(item.errors).some(val => typeof val === 'string' && val.trim() !== '');
    if (hasErrors) {
      //keep track of rows with active validation errors
      this.selectedErrorRowMap.set(currentIndex, item);
    } else if (this.selectedErrorRowMap.has(currentIndex)) {
      //if all errors in a row are fixed then dont keep track
      this.selectedErrorRowMap.delete(currentIndex);
    }
    // the topmost (lowest) index in the selected set
    this.topMostErrorRowIndex = this.selectedErrorRowMap.size ? Math.min(...this.selectedErrorRowMap.keys()) : null;

  }
  //CXDT-2314 - #7
  private updateAssistTimes(item: any): void {
    if (!this.lobbyView?.servicesProvided || this.lobbyView.servicesProvided.length < 2) return;
    const services = this.lobbyView.servicesProvided;
    const startIndex = services.findIndex(row => row === item);
    if (startIndex === -1 || startIndex >= services.length - 1) return;
    for (let i = startIndex + 1; i < services.length; i++) {
      const above = services[i - 1];
      const below = services[i];

      // Skip if above and below are in the same interaction
      if (above.interactionId === below.interactionId) {
        continue;
      }

      if (typeof above.completedLocalTime === 'string') {
        above.completedLocalTime = new Date(above.completedLocalTime);
      }
      if (below.waitTimeInt == null) {
        below.waitTimeInt = 0;
      }


      if (above.completedLocalTime instanceof Date && typeof below.waitTimeInt === 'number') {
        
        // Calculate new serviceStartedTime: completedLocalTime of above + waitTimeInt of below
        below.serviceStartedTime = new Date(above.completedLocalTime.getTime() + below.waitTimeInt * 1000);
        const serviceTime = typeof below.serviceTimeInt === 'number' ? below.serviceTimeInt : 0;
        const wrapUpTime = typeof below.wrapUpTimeInt === 'number' ? below.wrapUpTimeInt : 0;

        const totalSeconds = serviceTime + wrapUpTime;
        below.completedLocalTime = new Date(below.serviceStartedTime.getTime() + totalSeconds * 1000);
      }
      
    }
  }
  private updateServiceTimes(item: any, timeDiff: number): void {
    //cxdt-2314 - #4,#6
    const matchedService = this.lobbyView?.servicesProvided.find(sp => sp.serviceId === item.serviceId);
    var newWaitTimeInt = 0
    if (matchedService) {
      if (matchedService.waitTimeInt) {
        matchedService.waitTimeInt += Math.round(timeDiff / 1000);
        newWaitTimeInt = matchedService.waitTimeInt
      }else{
        matchedService.waitTimeInt = Math.round(timeDiff / 1000);
        newWaitTimeInt = matchedService.waitTimeInt
      }
    }
    this.lobbyView?.servicesProvided
      .filter(sp => sp.interactionId === item.interactionId)
      .forEach(sp => {
        if (sp.serviceStartedTime && sp.serviceProvidedId !== item.serviceProvidedId) {
          sp.serviceStartedTime = new Date(sp.serviceStartedTime.getTime() + timeDiff);
        }
        if (sp.serviceProvidedTime) {
          sp.serviceProvidedTime = new Date(sp.serviceProvidedTime.getTime() + timeDiff);
        }
        if (sp.lobbyAssistedTime) {
          sp.lobbyAssistedTime = new Date(sp.lobbyAssistedTime.getTime() + timeDiff);
        }
        if (sp.completedLocalTime) {
          sp.completedLocalTime = new Date(sp.completedLocalTime.getTime() + timeDiff);
        }
        if(newWaitTimeInt >= 0){//fix for CXDT-2444 issue #2
          sp.waitTimeInt = newWaitTimeInt;
        }
      });
  }

  private updateCompletedTimeIfLater(time: Date | number, item: any): void {
    const newTime = new Date(time);
    if (this.lobbyView) {
      if (!this.lobbyView?.completedLocalTime || newTime > this.lobbyView.completedLocalTime) {
        this.lobbyView.completedLocalTime = newTime;
        this.lobbyEditForm.get('completedTime')?.setValue(newTime);
        this.validateServiceStartedTime(item);
      }
    }
  }

  private validateAndUpdateCompleteTime(previousCompletedTime: Date, item: any): void {
    if (!previousCompletedTime) {
      // If previousCompletedTime is null or undefined, just update with the current completedLocalTime
      this.updateCompletedTimeIfLater(item.completedLocalTime, item);
      return;
    }
    if (this.lobbyView && previousCompletedTime.getTime() === this.lobbyView?.completedLocalTime?.getTime()) {
      let higherCompletedTimeValue = item.completedLocalTime;
      this.lobbyView?.servicesProvided.filter(sp => sp.interactionId !== item.interactionId).forEach(sp => {
        if(sp.completedLocalTime && sp.completedLocalTime.getTime() > higherCompletedTimeValue.getTime()){
          higherCompletedTimeValue = sp.completedLocalTime;
        }
      })
      this.lobbyView.completedLocalTime = higherCompletedTimeValue;
      this.lobbyEditForm.get('completedTime')?.setValue(higherCompletedTimeValue);
      this.validateServiceStartedTime(item);
    } else {
      this.updateCompletedTimeIfLater(item.completedLocalTime, item);
    }
  }

  private adjustTimes(services: any[], timeDiff: number, newWaitTime: string): void {
    services.forEach(sp => {
      sp.waitTime = newWaitTime;

      const timeFields = ['serviceStartedTime', 'serviceProvidedTime', 'lobbyAssistedTime', 'completedLocalTime'];
      timeFields.forEach(field => {
        if (sp[field] instanceof Date) {
          sp[field] = new Date(sp[field].getTime() + timeDiff * 1000);
        }
      });
    });
  }

  private validateServiceStartedTime(item: any): void {
  const checkInTimeValue = new Date(this.lobbyEditForm.get('checkInTime')?.value);
  const completedTimeValue = new Date(this.lobbyEditForm.get('completedTime')?.value);

  // Find index of the current item
  const allRows = this.lobbyView?.servicesProvided || [];
  const currentIndex = allRows.findIndex(row => row === item);

  if (currentIndex === 0) {
    // First interaction: only check against checkInTime
    if (item.serviceStartedTime >= checkInTimeValue) {
      item.errors.assistStartedError = '';
    } else {
      item.errors.assistStartedError = this.translateService.instant('_Report_Lobby_Assist_Time_Invalid_Error_Msg_');
    }
  }
}

  private updateSubsequentServices(item: any, previousCompletedTime: Date, timeDiff: number, ) : void{
    const sameInteractionServices = this.lobbyView?.servicesProvided
              .filter(sp => sp.interactionId === item.interactionId)
              .sort((a, b) => (a.serviceStartedTime as Date).getTime() - (b.serviceStartedTime as Date).getTime());
    if(sameInteractionServices){
      const changedIndex = sameInteractionServices.findIndex( sp => sp.serviceProvidedId === item.serviceProvidedId );   
      if (changedIndex !== -1) {
        for (let i = changedIndex + 1; i < sameInteractionServices.length; i++) {
          const sp = sameInteractionServices[i];
          if (sp.serviceStartedTime) {
            sp.serviceStartedTime = new Date(sp.serviceStartedTime.getTime() + timeDiff * 1000);
          }
          if (sp.serviceProvidedTime) {
            sp.serviceProvidedTime = new Date(sp.serviceProvidedTime.getTime() + timeDiff * 1000);
          }
        }

        this.lobbyView?.servicesProvided.filter(sp => sp.interactionId == item.interactionId).forEach(sp => {
            if(sp.completedLocalTime){
              sp.completedLocalTime = new Date(sp.completedLocalTime.getTime() + timeDiff * 1000);
            }
        })
        this.validateAndUpdateCompleteTime(previousCompletedTime, item)
      }
    }
  }

  isProductServiceFormValid(): boolean {
    return this.lobbyView?.servicesProvided.every(item =>
      item.serviceId &&
      item.assistedBy?.userId &&
      item.serviceStartedTime &&
      item.waitTimeInt != null &&
      item.serviceTimeInt != null &&
      item.wrapUpTimeInt != null &&
      !item.errors?.serviceId &&
      !item.errors?.assistedBy &&
      !item.errors?.serviceStartedTime &&
      !item.errors?.waitTime &&
      !item.errors?.serviceTime &&
      !item.errors?.wrapUpTime &&
      !item.errors?.assistStartedError
    ) ?? false

  }

  assignGroupIndices() {
    if (!this.lobbyView?.servicesProvided) return;

    // Group by interactionId
    const grouped = new Map<number, any[]>();

    for (const item of this.lobbyView.servicesProvided) {
      const id = item.interactionId ?? -1;

      if (!grouped.has(id)) {
        grouped.set(id, []);
      }
      grouped.get(id)!.push(item);
    }
    let index = 1;
    const newData: any[] = [];

    for (const [interactionId, groupItems] of grouped.entries()) {
      for (const item of groupItems) {
        item.groupIndex = index;
      }
      newData.push(...groupItems);
      index++;
    }

    this.lobbyView.servicesProvided = newData;
  }

}
